import type { Feature, FeatureCollection } from "geojson";
import { type ComponentType, type CSSProperties, type RefObject, useEffect, useMemo, useRef, useState } from "react";

import type { MeasureBoundary, MeasureKey, MeasureLine, MeasureNames, MeasureStyle, MeasureZone, Phase } from "../../api/types";
import { usePresence } from "../../components/presence";
import { tokenColor } from "../../components/theme";
import { useUi } from "../../state/ui";
import { watchDispositifPreview } from "../dossierWrites";
import { useDossierLanguage } from "../lineNames";
import { REVEAL_MS, revealPaint, revealStyle, useFadeIn, useMapRevealed } from "../reveal";
import { DATA_TRANSITION_MS, lerp, MAP_EXIT_MS, smoothstep, startDataTransition, usePhaseClock } from "../units/phaseTransition";
import { FRIEND_BLUE } from "../units/symbols";
import { useShownDeployment } from "./useOrientation";
import { dataSignature, type DataSignature, isDataTransition, signatureChanged } from "../units/UnitsLayers";
import {
  LIMA_OVERSHOOT_EDIT_PX,
  LIMA_OVERSHOOT_PX,
  limaOvershootRatio,
  labelPlacement,
  labelTransform,
  midpointAndBearing,
  overshootLines,
  overshootZoom,
} from "./corridorDisplay";
import type { CorridorFrame, LonLat } from "./corridorFrame";
import { useGestureState } from "./gestureState";
import { limaObstacles, useLimaGeometry } from "./limaChips";
import { useMeasureView } from "./measureView";
import { canAnimate, prefersReducedMotion, useLingering, useTicker } from "./motion";
import { namesShown } from "./MeasureNamesToggle";
import { useManoeuvre } from "./useManoeuvre";
import {
  BOUNDARIES,
  type Ends,
  lineCoords,
  linesAt,
  type PlacedTag,
  SAMPLES,
  type ScreenBox,
  stackTags,
  type TagObstacle,
  type TagStroke,
  tagWidthPx,
  zoneNameSpot,
  ZONE_NAME_TS,
} from "./zoMeasures";
import { mercatorProjector } from "./limaChips";

/** Lignes et zones de la zone d'opération (docs/15-fuseaux-doctrinaux.md §4.1) : LIMAR, LIMAV, limites des zones
 *  (ZOAR / ZOR, ZOR / ZOP), FLOT, CFL et FSCL du temps affiché, et noms des zones (ZO, ZOAR, ZOR, ZOP).
 *  - Couleur : bleu ami APP-6 (#5CBDFF) pour toutes, la couleur dit le camp ; le type se lit au nom et au motif du
 *    mémento : limites en trait plein (LIMAR et LIMAV épaisseur 3, limites de zone 2), FLOT en tireté, CFL en
 *    pointillé, FSCL en tiret-point. Une couche par motif (`line-dasharray` ne se pilote pas par les données).
 *  - Montage : après le fuseau et ses LIMA (au-dessus d'eux), sous les unités (`beforeId` sur leur première couche
 *    quand elle existe déjà). Couche de préhension invisible et large (`MEASURE_HIT_LAYER_ID`) : une ligne se saisit
 *    sans viser au pixel près.
 *  - Dépassement : comme les LIMA, chaque ligne (sauf la LIMAR et la LIMAV, qui ferment la ZO) dépasse les deux
 *    limites du fuseau d'au moins 12 px à l'écran (20 px en édition), recalculé au quart de niveau de zoom
 *    (`overshootLines`, limites affichées par CorridorLayers).
 *  - Puces : nom court dans la langue du dossier, au-delà du bout droit géographique de la ligne, dans son axe ;
 *    écartées vers l'extérieur, du minimum qui les dégage plus 4 px, tant que leur boîte recouvre celle d'une marque
 *    d'échelon, d'une puce de LIMA affichée (`useLimaGeometry` : leur place réelle, source unique) ou d'une autre
 *    puce, ou qu'elle touche une limite du fuseau ou une autre ligne (`stackTags`, boîtes à l'écran) ; variantes
 *    décalées de part et d'autre du bout et ramenées dans le cadre de la carte, la combinaison qui les éloigne le
 *    moins de leur bout l'emporte. Noms des zones sous les unités, dans la police des marques d'échelon, à une place
 *    libre des symboles des unités et des traits (`zoneNameSpot`), en bleu ami foncé du texte (jeton `friend`) sur
 *    halo blanc : lisibles sur la végétation.
 *  - Visibilité : `controlVisible.measures` règle les lignes (toujours affichées en mode édition, les poignées s'y
 *    posent), `controlVisible.measureNames` les puces et les noms des zones (bouton des noms, touche N, case des
 *    calques). Rien en CENTAC ni sans lignes calculées.
 *  - Mouvement : d'un temps à l'autre, la FLOT et la CFL glissent sur l'horloge des unités (2,5 s, chacun des 17
 *    points en `s`) ; à la réponse du serveur, transition de 900 ms (appariement par clé) ; une ligne qui apparaît
 *    ou disparaît se fond. Pendant un geste (fuseau retouché, ligne glissée, annulations en attente), le calque est
 *    recalculé côté client et suit sans transition. Fondus d'ensemble par `revealPaint`, aux durées partagées de la
 *    carte (`REVEAL_MS` à l'entrée, `MAP_EXIT_MS` à la sortie) ; mouvement réduit : passage direct. */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
  /** Carte : projection des puces (anti-recouvrement), couche des unités (`beforeId`). */
  mapRef: RefObject<{ getMap: () => unknown } | null>;
  /** Zoom courant : les puces se replacent à chaque changement (le calque se redessine avec la carte). */
  zoom: number;
  /** Cap de la carte (°) : place des puces au bout de leur ligne. */
  bearing?: number;
}

/** Couche de préhension des lignes de la ZO (MapCanvas, `pick.ts` : rang 1,5). */
export const MEASURE_HIT_LAYER_ID = "zo-measures-hit";
/** Noms des zones (ZO, ZOAR, ZOR, ZOP). */
export const ZONE_NAMES_LAYER_ID = "zo-zone-names";
/** Première couche des unités (UnitsLayers) : les lignes et les noms de zones passent dessous. */
const UNITS_FIRST_LAYER_ID = "units-moved";
const FONT = ["Open Sans Semibold"]; // police des marques d'échelon (CorridorLayers)
const WHITE = "#ffffff";
/** Abscisse transversale préférée des noms de zones (−1 : limite gauche, +1 : droite) : à l'écart de l'axe où se
 *  groupent les unités, du côté opposé aux puces des lignes (bout droit) ; premier des points essayés
 *  (`zoneNameSpot`). */
export const ZONE_NAME_T = ZONE_NAME_TS[0]!;
/** Boîte d'un symbole d'unité sur la carte (cadre de 49 px, échelon au-dessus, désignations dessous) : obstacle des
 *  noms de zones. */
export const UNIT_BOX_PX = { width: 64, height: 60 } as const;

/** Couche de chaque motif du mémento (`MeasureLine.style`) : tirets en multiples de l'épaisseur (2 px), comme le
 *  moteur de carte les compte ; le point d'une CFL ou d'une FSCL est un tiret nul à bouts ronds (2 px). */
export const MEASURE_PATTERNS: Readonly<Record<MeasureStyle, { id: string; dash: number[] | null; round: boolean }>> = {
  solid: { id: "zo-solid", dash: null, round: false },
  dashed: { id: "zo-flot", dash: [3.5, 2], round: false }, // 7 px, 4 px
  dotted: { id: "zo-cfl", dash: [0, 2.5], round: true }, // points de 2 px tous les 5 px
  dashdot: { id: "zo-fscl", dash: [3.5, 1.5, 0, 1.5], round: true }, // tiret de 7 px, point, 3 px entre eux
};

/** Halo de désignation d'une ligne voisine quand sa puce n'est pas montée (noms masqués) : sous la ligne. */
export const MEASURE_FLASH_LAYER_ID = "zo-flash";
/** Durée d'une demi-pulsation du halo : trois pulsations en 2,4 s, comme `flashAttention` (3 × 800 ms). */
export const FLASH_HALF_MS = 400;

/** Calendrier du halo : [instant (ms), allumé] ; trois pulsations, ou un halo fixe de 2,4 s en mouvement réduit. */
export function flashSchedule(reduced: boolean): [number, boolean][] {
  if (reduced) return [[0, true], [6 * FLASH_HALF_MS, false]];
  return Array.from({ length: 6 }, (_, i) => [i * FLASH_HALF_MS, i % 2 === 0] as [number, boolean]);
}

/** Ligne désignée (`gestureState.measureFlash`) et état du halo, au fil de son calendrier. */
function useLineFlash(): { key: MeasureKey | null; on: boolean } {
  const flash = useGestureState((s) => s.measureFlash);
  const [on, setOn] = useState(false);
  useEffect(() => {
    if (!flash) return;
    const timers = flashSchedule(prefersReducedMotion()).map(([at, value]) => window.setTimeout(() => setOn(value), at));
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      setOn(false);
    };
  }, [flash]);
  return { key: flash?.key ?? null, on };
}

/** Ordre de priorité des puces (les premières gardent leur place au bout de la ligne). */
const TAG_ORDER: readonly MeasureKey[] = ["LIMAR", "LIMAV", "FLOT", "CFL", "ZOR_ZOP", "ZOAR_ZOR", "FSCL"];

/** Ligne telle qu'elle est dessinée à un instant : tracé (interpolé pendant une transition) et opacité propre. */
export interface DrawnMeasure {
  key: MeasureKey;
  phase: Phase | null;
  ends: Ends;
  coordinates: LonLat[];
  style: MeasureStyle;
  weight: number;
  movable: boolean;
  names: MeasureNames;
  /** Opacité propre, de 0 à 1 (fondu d'une ligne qui apparaît ou disparaît). */
  o: number;
}

function drawnOf(line: MeasureLine, o = 1): DrawnMeasure {
  return {
    key: line.key,
    phase: line.phase,
    ends: [line.ends[0], line.ends[1]],
    coordinates: line.coordinates as LonLat[],
    style: line.style,
    weight: line.weight,
    movable: line.movable,
    names: line.names,
    o,
  };
}

/** Lignes dessinées à l'avancement adouci `eased` d'une transition partie de `from` (ce qui était dessiné) vers
 *  `target` (lignes du temps affiché), appariées par clé : une ligne gardée glisse (ses deux abscisses interpolées,
 *  tracé recalculé sur le repère `frame` — chacun des 17 points glisse en `s`), une ligne nouvelle se fond depuis 0,
 *  une ligne partie s'efface depuis l'opacité affichée et quitte la liste à la fin. Fonction pure. */
export function blendMeasures(
  from: ReadonlyMap<MeasureKey, DrawnMeasure>,
  target: readonly MeasureLine[],
  eased: number,
  frame: CorridorFrame | null,
): DrawnMeasure[] {
  const t = Math.min(1, Math.max(0, eased));
  const out: DrawnMeasure[] = [];
  const kept = new Set<MeasureKey>();
  for (const line of target) {
    kept.add(line.key);
    const before = from.get(line.key);
    if (!before || t >= 1) {
      out.push(drawnOf(line, before ? 1 : t >= 1 ? 1 : t));
      continue;
    }
    const ends: Ends = [lerp(before.ends[0], line.ends[0], t), lerp(before.ends[1], line.ends[1], t)];
    const same = before.ends[0] === line.ends[0] && before.ends[1] === line.ends[1];
    let coordinates: LonLat[];
    const sampled = before.coordinates.length === SAMPLES + 1 && line.coordinates.length === SAMPLES + 1;
    if (same) coordinates = line.coordinates as LonLat[];
    else if (frame && sampled) coordinates = lineCoords(frame, ends);
    else if (!sampled) coordinates = morphLine(before.coordinates, line.coordinates as LonLat[], t);
    else coordinates = line.coordinates.map((p, i) => lerpPoint(before.coordinates[i], p, t));
    out.push({ ...drawnOf(line), ends, coordinates, o: lerp(before.o, 1, t) });
  }
  if (t < 1) {
    for (const [key, before] of from) {
      if (kept.has(key)) continue;
      const o = before.o * (1 - t);
      if (o > 0.001) out.push({ ...before, o });
    }
  }
  return out;
}

/** Point à la fraction `f` de la longueur d'une polyligne (plan lon/lat, suffisant pour une transition). */
function alongLine(line: readonly LonLat[], f: number): LonLat {
  const lengths: number[] = [];
  let total = 0;
  for (let i = 0; i + 1 < line.length; i++) {
    const d = Math.hypot(line[i + 1]![0] - line[i]![0], line[i + 1]![1] - line[i]![1]);
    lengths.push(d);
    total += d;
  }
  if (!(total > 0)) return [line[0]![0], line[0]![1]];
  let goal = f * total;
  for (let i = 0; i < lengths.length; i++) {
    if (goal <= lengths[i]! || i === lengths.length - 1) {
      const r = lengths[i]! > 0 ? Math.min(1, goal / lengths[i]!) : 0;
      return [lerp(line[i]![0], line[i + 1]![0], r), lerp(line[i]![1], line[i + 1]![1], r)];
    }
    goal -= lengths[i]!;
  }
  return [line[line.length - 1]![0], line[line.length - 1]![1]];
}

/** Transition d'un tracé calé sur le terrain (corps d'armée : nombre de sommets libre, docs/08 §23) : les deux
 *  tracés rééchantillonnés à la même fraction de leur longueur (sommets des deux compris), puis interpolés ;
 *  la ligne glisse d'un tracé à l'autre sans saut à la fin de la transition. */
export function morphLine(from: readonly LonLat[], to: readonly LonLat[], t: number): LonLat[] {
  if (from.length < 2 || to.length < 2) return [...to];
  const fractions = new Set<number>();
  for (const line of [from, to]) {
    let total = 0;
    const steps = [0];
    for (let i = 0; i + 1 < line.length; i++) {
      total += Math.hypot(line[i + 1]![0] - line[i]![0], line[i + 1]![1] - line[i]![1]);
      steps.push(total);
    }
    for (const step of steps) fractions.add(total > 0 ? step / total : 0);
  }
  return [...fractions].sort((a, b) => a - b).map((f) => {
    const a = alongLine(from, f);
    const b = alongLine(to, f);
    return [lerp(a[0], b[0], t), lerp(a[1], b[1], t)];
  });
}

function lerpPoint(a: readonly number[] | undefined, b: readonly number[], t: number): LonLat {
  if (!a) return [b[0]!, b[1]!];
  return [lerp(a[0]!, b[0]!, t), lerp(a[1]!, b[1]!, t)];
}

const collection = (features: Feature[]): FeatureCollection => ({ type: "FeatureCollection", features });

/** Limites entre subordonnés (corps d'armée : entre ses deux divisions, docs/08 §23) : le trait plein, et la marque
 *  d'échelon (« XX ») au milieu, tournée sur la ligne comme celles des limites du fuseau. */
export function boundaryFeatures(boundaries: readonly MeasureBoundary[] | null | undefined): {
  lines: FeatureCollection;
  marks: FeatureCollection;
} {
  const list = (boundaries ?? []).filter((b) => b.coordinates.length >= 2);
  return {
    lines: collection(
      list.map((b) => ({ type: "Feature", geometry: { type: "LineString", coordinates: b.coordinates }, properties: { key: b.key } })),
    ),
    marks: collection(
      list.flatMap((b) => {
        const found = midpointAndBearing(b.coordinates);
        if (!found) return [];
        const [at, bearing] = found;
        return [{ type: "Feature", geometry: { type: "Point", coordinates: at }, properties: { text: b.echelon, rotation: bearing + 90 } }];
      }),
    ),
  };
}
/** Limites entre subordonnés : couches du trait et de la marque d'échelon. */
export const BOUNDARY_LAYER_ID = "zo-boundaries";
export const BOUNDARY_MARK_LAYER_ID = "zo-boundaries-echelon";

/** Empreinte de ce qui est dessiné : même empreinte, mêmes entités (le moteur ne reçoit rien de nouveau). */
export function drawnSignature(lines: readonly DrawnMeasure[]): string {
  return lines
    .map((line) => {
      const first = line.coordinates[0];
      const last = line.coordinates[line.coordinates.length - 1];
      return [line.key, line.phase, line.ends[0], line.ends[1], line.o, line.style, first?.[0], first?.[1], last?.[0], last?.[1]].join(":");
    })
    .join("|");
}

/** Entités des lignes : une par ligne dessinée, avec son motif, son épaisseur et son opacité propre. */
export function measureFeatures(lines: readonly DrawnMeasure[]): FeatureCollection {
  return collection(
    lines
      .filter((line) => line.coordinates.length >= 2)
      .map((line) => ({
        type: "Feature",
        geometry: { type: "LineString", coordinates: line.coordinates },
        properties: { key: line.key, phase: line.phase, style: line.style, weight: line.weight, movable: line.movable, o: line.o },
      })),
  );
}

/** Entités des noms de zones, dans la langue du dossier. */
export function zoneFeatures(zones: readonly MeasureZone[], language: "fr" | "en"): FeatureCollection {
  return collection(
    zones.map((zone) => ({
      type: "Feature",
      geometry: { type: "Point", coordinates: zone.label },
      properties: { key: zone.key, name: zone.names[language].short },
    })),
  );
}

/** Lignes affichées : tracé prolongé pour dépasser les deux limites du fuseau (`rails`) d'au moins `minPx` au zoom
 *  donné, comme les LIMA ; la LIMAR et la LIMAV, qui ferment la ZO avec les limites, s'arrêtent à leurs coins. Sans
 *  limites, tracés tels quels. */
export function overshootMeasures(
  lines: readonly DrawnMeasure[],
  rails: readonly (readonly LonLat[])[],
  minPx: number,
  zoom: number,
  widthRatio?: number,
): DrawnMeasure[] {
  const boundaries = rails.filter((rail) => rail.length >= 2).map((rail) => [...rail]);
  if (boundaries.length === 0) return [...lines];
  return lines.map((line) => {
    if (BOUNDARIES.includes(line.key)) return line;
    const [out] = overshootLines([{ coords: line.coordinates }], boundaries, minPx, zoom, widthRatio);
    return out && out.coords !== line.coordinates ? { ...line, coordinates: out.coords } : line;
  });
}

/** Cadre de la carte à l'écran (pixels de `map.project`), en retrait de 4 px : les puces y restent de préférence. */
function viewportOf(map: { getContainer?: () => HTMLElement }): ScreenBox | undefined {
  const el = map.getContainer?.();
  if (!el || !el.clientWidth || !el.clientHeight) return undefined;
  return { x0: 4, y0: 4, x1: el.clientWidth - 4, y1: el.clientHeight - 4 };
}

/** Puces posées sans recouvrement (ordre de priorité `TAG_ORDER`), à l'écran `project`, largeur estimée d'après le
 *  nom court dans la langue du dossier ; traits à ne pas toucher : `strokes` (limites du fuseau, LIMA affichées) et
 *  les autres lignes de la ZO ; variantes décalées de part et d'autre du bout, de préférence dans `viewport` (cadre
 *  de la carte). */
export function placeTags(
  lines: readonly DrawnMeasure[],
  obstacles: readonly TagObstacle[],
  project: (p: LonLat) => readonly [number, number],
  language: "fr" | "en" = "fr",
  strokes: readonly TagStroke[] = [],
  viewport?: ScreenBox,
): PlacedTag[] {
  const ordered = [...lines]
    .filter((line) => line.coordinates.length >= 2)
    .sort((a, b) => TAG_ORDER.indexOf(a.key) - TAG_ORDER.indexOf(b.key));
  return stackTags(
    ordered.map((line) => ({ id: line.key, line: line.coordinates, widthPx: tagWidthPx(line.names[language].short) })),
    obstacles,
    project,
    { strokes: [...strokes, ...ordered.map((line) => ({ id: line.key, line: line.coordinates }))], lateral: true, viewport },
  );
}

/** Puce d'une ligne de la ZO : charte des puces de LIMA (puce de l'interface, nom en bleu ami). Transparente aux
 *  gestes : la ligne se saisit par sa couche de préhension. `data-measure-tag` : clé de la ligne (désignée quand une
 *  ligne glissée bute sur elle). */
function MeasureTag({ name, tag }: { name: string; tag: MeasureKey }) {
  return (
    <div
      data-measure-tag={tag}
      className="pointer-events-none flex h-5 items-center rounded-md border border-line-strong bg-surface px-1.5 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.12)]">
      <span className="text-[12px] leading-none font-semibold text-friend">{name}</span>
    </div>
  );
}

/** Lignes et noms affichés : dossier ouvert, lignes de la ZO présentes et entrées en fondu ; lignes selon la case
 *  « Lignes et zones de la ZO » (toujours en mode édition : les poignées s'y posent), noms quand le bouton des noms
 *  est enfoncé (`namesShown` : cases des noms et des lignes cochées), jamais sans les lignes. */
export function measureVisibility(o: {
  revealed: boolean;
  has: boolean;
  entered: boolean;
  editing: boolean;
  measures: boolean;
  measureNames: boolean;
}): { lines: boolean; names: boolean } {
  const lines = o.revealed && o.has && o.entered && (o.measures || o.editing);
  // Noms : même prédicat que le bouton des noms et la case des calques (`namesShown`) ; en mode édition avec la case
  // des lignes décochée, les lignes restent pour les poignées mais sans noms (le bouton est relâché).
  return { lines, names: lines && namesShown(o) };
}

/** Carte au plus juste pour la projection et la recherche d'une couche (commun à Mapbox GL JS et MapLibre GL JS). */
interface MapLike {
  project: (p: LonLat) => { x: number; y: number };
  getLayer: (id: string) => unknown;
  getContainer?: () => HTMLElement;
}

/** Délai au-delà duquel une réponse retenue (transition de données pas encore ouverte) est montrée sans transition. */
const HOLD_MS = 300;

interface Plan {
  /** Dossier, camp et OPO du plan : un autre dossier se dessine sans transition. */
  dossier: string | null;
  /** Lignes dessinées au départ du plan. */
  from: Map<MeasureKey, DrawnMeasure>;
  /** Transition de l'horloge commune (temps, réponse du serveur) ; null : plan local (`MAP_EXIT_MS`). */
  clock: number | null;
  /** Aperçu : le calque suit le geste, sans transition. */
  direct: boolean;
  seen: number;
  started: number;
  inputs: unknown[];
}

export function ZoMeasuresLayers({ Source, Layer, Marker, mapRef, zoom, bearing = 0 }: EngineParts) {
  const { data, isPlaceholderData } = useShownDeployment(); // aperçu du sens de la manœuvre (docs/14 §2.14)
  const phase = useUi((s) => s.phase);
  const visible = useUi((s) => s.controlVisible);
  const exerciseId = useUi((s) => s.deploymentParams?.exercise_id ?? null);
  const editing = useManoeuvre((s) => s.editing);
  const revealed = useMapRevealed();
  const language = useDossierLanguage();
  const view = useMeasureView(data);
  const id = usePhaseClock((s) => s.id);
  const progress = usePhaseClock((s) => s.progress);

  // Annulations rapides (⌘Z) : les lignes visées suivent tout de suite, avant la réponse (docs/15 §3.8).
  useEffect(() => {
    const off = watchDispositifPreview((state) =>
      useGestureState.getState().setHistoryMeasures(state ? { measures: state.measures ?? null } : null),
    );
    return () => {
      off();
      useGestureState.getState().setHistoryMeasures(null);
    };
  }, []);

  const displayed = useRef(new Map<MeasureKey, DrawnMeasure>());
  const plan = useRef<Plan | null>(null);
  const signature = useRef<DataSignature | null>(null);
  // Réponse du serveur reçue, transition de données pas encore ouverte (UnitsLayers, ou ce calque, l'ouvre dans son
  // effet) : les lignes gardent ce qu'elles affichent jusque-là, pour glisser ensuite depuis cette position.
  const held = useRef<{ id: number; at: number } | null>(null);
  const [ticking, setTicking] = useState(false);
  const tick = useTicker(ticking);
  const now = ticking ? tick : performance.now();

  const next = data && !isPlaceholderData ? dataSignature(data, exerciseId) : null;
  if (next && signatureChanged(signature.current, next)) {
    if (isDataTransition(signature.current, next) && !view?.preview) held.current = { id, at: now };
    signature.current = next;
  }
  if (held.current !== null && (held.current.id !== id || now - held.current.at > HOLD_MS)) held.current = null;
  useEffect(() => {
    if (held.current && held.current.id === usePhaseClock.getState().id) startDataTransition(DATA_TRANSITION_MS, useUi.getState().phase);
  }, [next?.version, next?.exercise, next?.map, next?.opo]); // eslint-disable-line react-hooks/exhaustive-deps
  // Sécurité : sans transition ouverte, un rendu forcé à l'échéance libère les lignes retenues.
  const [, forceRender] = useState(0);
  const heldAt = held.current?.at ?? null;
  useEffect(() => {
    if (heldAt === null) return;
    const timer = window.setTimeout(() => forceRender((n) => n + 1), HOLD_MS + 20);
    return () => window.clearTimeout(timer);
  }, [heldAt]);

  const target = view ? linesAt(view.measures.lines, phase) : [];
  const dossier = next ? `${next.exercise}|${next.map}|${next.opo}` : null;
  const inputs: unknown[] = [view?.measures ?? null, phase];
  let drawn: DrawnMeasure[];
  if (held.current !== null || !view) {
    // Réponse retenue, ou calque qui s'en va (CENTAC, fuseau retiré) : les lignes restent, le fondu d'ensemble les
    // efface.
    drawn = [...displayed.current.values()];
  } else {
    const previous = plan.current;
    const clockChanged = previous !== null && previous.seen !== id;
    const inputsChanged = previous === null || previous.inputs.some((value, i) => value !== inputs[i]);
    if (clockChanged || inputsChanged) {
      const direct = view.preview || !canAnimate() || previous === null || previous.dossier !== dossier;
      plan.current = {
        dossier,
        from: new Map(displayed.current),
        clock: !direct && clockChanged ? id : !direct && previous?.clock === id && progress < 1 ? id : null,
        direct,
        seen: id,
        started: now,
        inputs,
      };
    }
    const current = plan.current!;
    let eased = 1;
    if (!current.direct) {
      if (current.clock !== null) eased = current.clock === id ? smoothstep(progress) : 1;
      else eased = smoothstep(Math.min(1, Math.max(0, (now - current.started) / MAP_EXIT_MS)));
    }
    drawn = current.direct ? target.map((line) => drawnOf(line)) : blendMeasures(current.from, target, eased, view.frame);
  }
  displayed.current = new Map(drawn.map((line) => [line.key, line]));
  const animating = held.current === null && plan.current !== null && !plan.current.direct && plan.current.clock === null &&
    now - plan.current.started < MAP_EXIT_MS;
  useEffect(() => {
    if (animating !== ticking) setTicking(animating);
  });

  // Visibilité : lignes (toujours en édition), noms ; entrée en fondu même quand le calque arrive après le dossier.
  const has = view !== null;
  const mounted = useLingering(has, MAP_EXIT_MS);
  const entered = useFadeIn(has ? "zo" : null);
  const { lines: linesOn, names: namesOn } = measureVisibility({
    revealed,
    has,
    entered,
    editing,
    measures: visible.measures,
    measureNames: visible.measureNames,
  });
  const linesLinger = useLingering(linesOn, MAP_EXIT_MS);
  const namesLinger = useLingering(namesOn, MAP_EXIT_MS);
  // Puces : montées le temps de leur sortie, créées transparentes puis montées à leur opacité (fondu d'entrée).
  const tags = usePresence(namesOn, MAP_EXIT_MS);
  const tagsIn = useFadeIn(namesOn ? "names" : null);
  const tagsShown = namesOn && tagsIn && !tags.closing;
  const fading = drawn.some((line) => line.o < 0.999);
  // Calque des LIMA tel qu'il est affiché (source unique) : limites du dépassement, obstacles et traits des puces.
  const lima = useLimaGeometry((s) => s.geometry);
  const atZoom = overshootZoom(zoom);
  const minPx = editing ? LIMA_OVERSHOOT_EDIT_PX : LIMA_OVERSHOOT_PX;
  // Tracés affichés : dépassement des deux limites, comme les LIMA (au quart de niveau de zoom ; hors édition :
  // plafonné à 5 % de la largeur locale du fuseau, camps compris).
  const widthRatio = limaOvershootRatio(editing);
  const shown = overshootMeasures(drawn, lima.rails, minPx, atZoom, widthRatio);
  // Entités renvoyées au moteur seulement quand le dessin change (pas à chaque rendu du calque).
  const signatureOfDrawn = drawnSignature(shown);
  const drawnRef = useRef(shown);
  drawnRef.current = shown;
  const lineFeatures = useMemo(() => measureFeatures(drawnRef.current), [signatureOfDrawn]); // eslint-disable-line react-hooks/exhaustive-deps
  const flash = useLineFlash();
  const zoneData = view?.measures.zones ?? null;
  const zoneLines = view?.measures.lines ?? null;
  const boundaryData = view?.measures.boundaries ?? null;
  const boundaries = useMemo(() => boundaryFeatures(boundaryData), [boundaryData]);
  const zoneFrame = view?.frame ?? null;
  // Noms des zones : à une place libre des symboles des unités (à tous les temps : le nom ne bouge pas d'un temps à
  // l'autre) et des traits (limites, LIMA, lignes de la ZO), au quart de niveau de zoom et au cap de la carte.
  const units = data?.units;
  const unitObstacles = useMemo<TagObstacle[]>(
    () =>
      (units ?? []).flatMap((unit) =>
        unit.hidden
          ? []
          : Object.values(unit.positions).flatMap((pos) =>
              pos ? [{ at: [pos.lon, pos.lat] as LonLat, sizePx: UNIT_BOX_PX.width, heightPx: UNIT_BOX_PX.height }] : [],
            ),
      ),
    [units],
  );
  // Lignes de la ZO qui ne changent pas d'un temps à l'autre (sans la FLOT ni la CFL) : le nom reste en place quand
  // elles glissent sur l'horloge des unités.
  const fixedLines = shown.filter((line) => line.phase === null);
  const fixedSignature = fixedLines.map((line) => `${line.key}:${line.ends[0]}:${line.ends[1]}:${line.coordinates.length}`).join("|");
  const fixedRef = useRef(fixedLines);
  fixedRef.current = fixedLines;
  const nameStrokes = useMemo<TagStroke[]>(
    () => [...lima.rails, ...lima.lines, ...fixedRef.current.map((line) => line.coordinates)].map((line) => ({ line })),
    [lima, fixedSignature],
  );
  const zones = useMemo(() => {
    const project = mercatorProjector(atZoom, bearing);
    const taken: ScreenBox[] = [];
    const placed = (zoneData ?? []).map((zone) => {
      const spot =
        zoneFrame && zoneLines
          ? zoneNameSpot(zoneFrame, zoneLines, zone.between, zone.names[language].short, project, {
              obstacles: unitObstacles,
              strokes: nameStrokes,
              taken,
            })
          : null;
      if (!spot) return zone;
      taken.push(spot.box);
      return { ...zone, label: [spot.at[0], spot.at[1]] as [number, number] };
    });
    return zoneFeatures(placed, language);
  }, [zoneData, zoneLines, zoneFrame, language, atZoom, bearing, unitObstacles, nameStrokes]);

  // Puces : obstacles (marques d'échelon, puces des LIMA à leur place affichée) et traits à ne pas toucher.
  const obstacles = useMemo(() => limaObstacles(lima), [lima]);
  const strokes = useMemo<TagStroke[]>(
    () => [...lima.rails.map((line) => ({ line })), ...lima.lines.map((line) => ({ line }))],
    [lima],
  );

  if (!mounted) return null;
  const map = (mapRef.current?.getMap() as MapLike | undefined) ?? null;
  const beforeId = map?.getLayer(UNITS_FIRST_LAYER_ID) ? UNITS_FIRST_LAYER_ID : undefined;

  /** Peinture d'une couche de lignes : fondu d'ensemble du moteur (`revealPaint`), ou, pendant le fondu d'une ligne,
   *  opacité par entité imposée à chaque image. */
  const linePaint = (dash: number[] | null): Record<string, unknown> => {
    const base: Record<string, unknown> = { "line-color": FRIEND_BLUE, "line-width": ["get", "weight"] };
    if (dash) base["line-dasharray"] = dash;
    if (fading)
      return { ...base, "line-opacity": linesOn ? ["get", "o"] : 0, "line-opacity-transition": { duration: 0, delay: 0 } };
    return revealPaint("line", base, linesOn, linesOn ? REVEAL_MS : MAP_EXIT_MS);
  };
  const tagStyle = (o: number): CSSProperties =>
    fading ? { opacity: tagsShown ? o : 0 } : revealStyle(tagsShown, tagsShown ? REVEAL_MS : MAP_EXIT_MS);

  const placed =
    tags.mounted && map
      ? placeTags(
          shown,
          obstacles,
          (p) => {
            const q = map.project(p);
            return [q.x, q.y];
          },
          language,
          strokes,
          viewportOf(map),
        )
      : [];
  const byKey = new Map(drawn.map((line) => [line.key, line]));

  return (
    <>
      <Source id="gx-zo-lines" type="geojson" data={lineFeatures}>
        {/* Halo de désignation (butée sur une ligne sans puce montée), sous les lignes, fondu à chaque pulsation. */}
        <Layer
          id={MEASURE_FLASH_LAYER_ID}
          type="line"
          beforeId={beforeId}
          filter={["==", ["get", "key"], flash.key ?? ""]}
          layout={{ visibility: linesLinger && flash.key ? "visible" : "none", "line-cap": "round", "line-join": "round" }}
          paint={revealPaint(
            "line",
            { "line-color": FRIEND_BLUE, "line-width": ["+", ["get", "weight"], 8], "line-blur": 3, "line-opacity": 0.55 },
            flash.on && linesOn,
            FLASH_HALF_MS,
          )}
        />
        {(Object.keys(MEASURE_PATTERNS) as MeasureStyle[]).map((style) => {
          const pattern = MEASURE_PATTERNS[style];
          return (
            <Layer
              key={pattern.id}
              id={pattern.id}
              type="line"
              beforeId={beforeId}
              filter={["==", ["get", "style"], style]}
              layout={{
                visibility: linesLinger ? "visible" : "none",
                "line-join": "round",
                "line-cap": pattern.round ? "round" : "butt",
              }}
              paint={linePaint(pattern.dash)}
            />
          );
        })}
        {/* Couche de préhension, invisible et large ; absente quand les lignes sont masquées. */}
        <Layer
          id={MEASURE_HIT_LAYER_ID}
          type="line"
          beforeId={beforeId}
          layout={{ visibility: linesOn ? "visible" : "none", "line-cap": "round" }}
          paint={revealPaint("line", { "line-color": "#000000", "line-width": 18, "line-opacity": 0.01 }, linesOn)}
        />
      </Source>
      {/* Limite entre les divisions (corps d'armée) : trait plein et marque d'échelon, comme les limites du fuseau. */}
      <Source id="gx-zo-boundaries" type="geojson" data={boundaries.lines}>
        <Layer
          id={BOUNDARY_LAYER_ID}
          type="line"
          beforeId={beforeId}
          layout={{ visibility: linesLinger ? "visible" : "none", "line-join": "round" }}
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 2 }, linesOn, linesOn ? REVEAL_MS : MAP_EXIT_MS)}
        />
      </Source>
      <Source id="gx-zo-boundary-marks" type="geojson" data={boundaries.marks}>
        <Layer
          id={BOUNDARY_MARK_LAYER_ID}
          type="symbol"
          beforeId={beforeId}
          layout={{
            visibility: linesLinger ? "visible" : "none",
            "text-field": ["get", "text"],
            "text-font": FONT,
            "text-size": 22,
            "text-rotate": ["get", "rotation"],
            "text-rotation-alignment": "map",
            "text-allow-overlap": true,
            "text-ignore-placement": true,
          }}
          paint={revealPaint("symbol", { "text-color": FRIEND_BLUE }, linesOn, linesOn ? REVEAL_MS : MAP_EXIT_MS)}
        />
      </Source>
      <Source id="gx-zo-zones" type="geojson" data={zones}>
        <Layer
          id={ZONE_NAMES_LAYER_ID}
          type="symbol"
          beforeId={beforeId}
          layout={{
            visibility: namesLinger ? "visible" : "none",
            "text-field": ["get", "name"],
            "text-font": FONT,
            "text-size": 13,
            "text-letter-spacing": 0.08,
            "text-allow-overlap": false,
          }}
          paint={revealPaint(
            "symbol",
            { "text-color": tokenColor("friend"), "text-halo-color": WHITE, "text-halo-width": 2 },
            namesOn,
            namesOn ? REVEAL_MS : MAP_EXIT_MS,
          )}
        />
      </Source>
      {tags.mounted &&
        placed.map((tag) => {
          const line = byKey.get(tag.id as MeasureKey);
          if (!line) return null;
          return (
            <Marker key={tag.id} longitude={tag.at[0]} latitude={tag.at[1]} anchor="center" style={{ pointerEvents: "none" }}>
              {/* Fondu sur le contenu : le moteur réécrit l'opacité de l'élément du repère (occultation). */}
              <div
                style={{
                  ...tagStyle(line.o),
                  transform: `translate(${tag.offsetPx[0]}px, ${tag.offsetPx[1]}px) ${labelTransform(labelPlacement(tag.bearing, bearing))}`,
                }}>
                <MeasureTag name={line.names[language].short} tag={line.key} />
              </div>
            </Marker>
          );
        })}
    </>
  );
}
